import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Stack,
  Chip,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  InputAdornment,
  alpha,
  useTheme,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  ArrowBack,
  Search,
  Refresh,
  FilterList,
  ExpandMore,
  ExpandLess,
  FileDownload,
  Description,
  Close,
  ContentCopy,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { format, parseISO } from 'date-fns';
import JSZip from 'jszip';
import { api } from '@/services/api';
import toast from 'react-hot-toast';

// Types based on Sicoob movimentação response (real structure)
interface BoletoMovimento {
  // Identificadores
  numeroTitulo: number;
  seuNumero?: string;
  codigoBarras?: string;
  numeroCliente?: number;
  numeroContrato?: number;
  numeroContratoCobranca?: number;
  numeroContaCorrente?: number;
  modalidade?: number;

  // Datas
  dataVencimentoTitulo?: string;
  dataLiquidacao?: string;
  dataMovimentoLiquidacao?: string;
  dataPrevisaoCredito?: string;
  dataInicioMovimento?: string;
  dataFimMovimento?: string;

  // Valores
  valorTitulo?: number;
  valorLiquido?: number;
  valorDesconto?: number;
  valorAbatimento?: number;
  valorMora?: number;
  valorTarifaMovimento?: number;

  // Informações da operação
  siglaMovimento?: string;
  tipoOpFinanceira?: string;
  idTipoOpFinanceira?: number;
  tipoCarteiraOpCredito?: string;
  numeroBancoRecebedor?: number;
  numeroAgenciaRecebedora?: number;

  // Additional fields
  [key: string]: any;
}

type Order = 'asc' | 'desc';

interface HeadCell {
  id: keyof BoletoMovimento | string;
  label: string;
  numeric: boolean;
  format?: (value: any) => string;
}

// Dynamic columns based on movement type
const getHeadCells = (tipo: number): HeadCell[] => {
  // Common columns for all types
  const common: HeadCell[] = [
    { id: 'numeroTitulo', label: 'Nosso Número', numeric: true },
    { id: 'seuNumero', label: 'Seu Número', numeric: false },
    { id: 'dataVencimentoTitulo', label: 'Vencimento', numeric: false },
    { id: 'valorTitulo', label: 'Valor Título', numeric: true },
  ];

  switch (tipo) {
    case 1: // Entrada
      return [
        ...common,
        { id: 'dataEmissaoDocumento', label: 'Emissão', numeric: false },
        { id: 'dataMovimentoEntrada', label: 'Entrada', numeric: false },
        { id: 'dataLimitePagamento', label: 'Limite Pgto', numeric: false },
        { id: 'valorTarifaMovimento', label: 'Tarifa', numeric: true },
        { id: 'hasQrCode', label: 'PIX', numeric: false },
      ];
    case 2: // Prorrogação
    case 3: // A Vencer
      return [
        ...common,
        { id: 'dataLimitePagamento', label: 'Limite Pgto', numeric: false },
        { id: 'valorTarifaMovimento', label: 'Tarifa', numeric: true },
      ];
    case 4: // Vencido
      return [
        ...common,
        { id: 'qtdDiasVencido', label: 'Dias Vencido', numeric: true },
        { id: 'valorMultaAtual', label: 'Multa', numeric: true },
        { id: 'valorMoraAtual', label: 'Mora', numeric: true },
        { id: 'valorTituloAtualizado', label: 'Valor Atual', numeric: true },
        { id: 'dataLimitePagamento', label: 'Limite Pgto', numeric: false },
      ];
    case 5: // Liquidação
      return [
        ...common,
        { id: 'dataLiquidacao', label: 'Liquidação', numeric: false },
        { id: 'dataPrevisaoCredito', label: 'Prev. Crédito', numeric: false },
        { id: 'valorDesconto', label: 'Desconto', numeric: true },
        { id: 'valorMora', label: 'Mora', numeric: true },
        { id: 'valorLiquido', label: 'Valor Líquido', numeric: true },
        { id: 'valorTarifaMovimento', label: 'Tarifa', numeric: true },
        { id: 'tipoOpFinanceira', label: 'Forma Pgto', numeric: false },
      ];
    case 6: // Baixa
      return [
        ...common,
        { id: 'dataLiquidacao', label: 'Data Baixa', numeric: false },
        { id: 'tipoOpFinanceira', label: 'Motivo', numeric: false },
        { id: 'valorTarifaMovimento', label: 'Tarifa', numeric: true },
      ];
    case 7: // Vinculado
      return [
        ...common,
        { id: 'valorTarifaMovimento', label: 'Tarifa', numeric: true },
      ];
    default:
      return common;
  }
};

const TIPO_MOVIMENTO_LABELS: Record<number, string> = {
  1: 'Entrada',
  2: 'Prorrogação',
  3: 'A Vencer',
  4: 'Vencido',
  5: 'Liquidação',
  6: 'Baixa',
  7: 'Vinculado',
};

const BoletoMovimentacaoViewerPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const codigoSolicitacao = searchParams.get('codigo');
  const idArquivo = searchParams.get('arquivo');
  const tipoMovimento = parseInt(searchParams.get('tipo') || '5');

  // Data states
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [boletos, setBoletos] = useState<BoletoMovimento[]>([]);
  const [fileName, setFileName] = useState<string>('');

  // Table states
  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<string>('dataLiquidacao');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Filter states
  const [showFilters, setShowFilters] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDataInicio, setFilterDataInicio] = useState<Date | null>(null);
  const [filterDataFim, setFilterDataFim] = useState<Date | null>(null);
  const [filterSituacao, setFilterSituacao] = useState<string>('');
  const [filterValorMin, setFilterValorMin] = useState<string>('');
  const [filterValorMax, setFilterValorMax] = useState<string>('');

  // Detail modal state
  const [selectedRecord, setSelectedRecord] = useState<BoletoMovimento | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  // Dynamic columns based on movement type
  const headCells = useMemo(() => getHeadCells(tipoMovimento), [tipoMovimento]);

  useEffect(() => {
    if (codigoSolicitacao && idArquivo) {
      loadData();
    } else {
      setError('Parâmetros inválidos. Código da solicitação e ID do arquivo são obrigatórios.');
      setLoading(false);
    }
  }, [codigoSolicitacao, idArquivo]);

  const loadData = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.get('/boletos/movimentacoes/download', {
        params: { codigoSolicitacao, idArquivo },
      });

      if (response?.success && response.data?.resultado) {
        const { arquivo, nomeArquivo } = response.data.resultado;
        setFileName(nomeArquivo);

        // Decode base64 and extract ZIP
        const binaryString = atob(arquivo);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }

        const zip = await JSZip.loadAsync(bytes);
        const jsonFiles = Object.keys(zip.files).filter(f => f.endsWith('.json'));

        if (jsonFiles.length === 0) {
          throw new Error('Nenhum arquivo JSON encontrado no ZIP');
        }

        // Read and parse the first JSON file
        const jsonContent = await zip.files[jsonFiles[0]].async('string');
        const data = JSON.parse(jsonContent);

        // Handle different response formats
        let boletosList: BoletoMovimento[] = [];
        if (Array.isArray(data)) {
          boletosList = data;
        } else if (data.boletos && Array.isArray(data.boletos)) {
          boletosList = data.boletos;
        } else if (data.resultado && Array.isArray(data.resultado)) {
          boletosList = data.resultado;
        } else {
          // Try to find any array in the response
          for (const key of Object.keys(data)) {
            if (Array.isArray(data[key])) {
              boletosList = data[key];
              break;
            }
          }
        }

        setBoletos(boletosList);
        toast.success(`${boletosList.length} registros carregados`);
      } else {
        setError(response?.error?.message || 'Erro ao carregar dados');
      }
    } catch (err: any) {
      console.error('Error loading movimentacao data:', err);
      setError(err.message || 'Erro ao processar arquivo de movimentação');
    } finally {
      setLoading(false);
    }
  };

  // Filtered and sorted data
  const filteredBoletos = useMemo(() => {
    return boletos.filter(boleto => {
      // Search term filter
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        const matchNumeroTitulo = String(boleto.numeroTitulo || '').includes(search);
        const matchSeuNumero = String(boleto.seuNumero || '').toLowerCase().includes(search);
        const matchCodigoBarras = String(boleto.codigoBarras || '').includes(search);
        if (!matchNumeroTitulo && !matchSeuNumero && !matchCodigoBarras) {
          return false;
        }
      }

      // Date range filter (liquidação)
      if (filterDataInicio && boleto.dataLiquidacao) {
        const dataLiq = new Date(boleto.dataLiquidacao);
        if (dataLiq < filterDataInicio) return false;
      }
      if (filterDataFim && boleto.dataLiquidacao) {
        const dataLiq = new Date(boleto.dataLiquidacao);
        if (dataLiq > filterDataFim) return false;
      }

      // Tipo operação filter
      if (filterSituacao && boleto.tipoOpFinanceira !== filterSituacao) {
        return false;
      }

      // Value range filter (valor líquido)
      const valor = boleto.valorLiquido || boleto.valorTitulo || 0;
      if (filterValorMin && valor < parseFloat(filterValorMin)) return false;
      if (filterValorMax && valor > parseFloat(filterValorMax)) return false;

      return true;
    });
  }, [boletos, searchTerm, filterDataInicio, filterDataFim, filterSituacao, filterValorMin, filterValorMax]);

  // Sorted data
  const sortedBoletos = useMemo(() => {
    const getValue = (boleto: BoletoMovimento, key: string): any => {
      return (boleto as any)[key];
    };

    return [...filteredBoletos].sort((a, b) => {
      const aValue = getValue(a, orderBy);
      const bValue = getValue(b, orderBy);

      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return order === 'asc' ? aValue - bValue : bValue - aValue;
      }

      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();
      return order === 'asc' ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr);
    });
  }, [filteredBoletos, order, orderBy]);

  // Summary statistics - dynamic based on movement type
  const summary = useMemo(() => {
    const total = filteredBoletos.length;
    const valorTituloTotal = filteredBoletos.reduce((sum, b) => sum + (b.valorTitulo || 0), 0);
    const tarifaTotal = filteredBoletos.reduce((sum, b) => sum + (b.valorTarifaMovimento || 0), 0);

    // Type-specific calculations
    let extra: Record<string, number> = {};

    switch (tipoMovimento) {
      case 1: // Entrada
        extra.comPix = filteredBoletos.filter(b => b.qrCode).length;
        extra.semPix = filteredBoletos.filter(b => !b.qrCode).length;
        break;
      case 4: // Vencido
        extra.valorMultaTotal = filteredBoletos.reduce((sum, b) => sum + (b.valorMultaAtual || 0), 0);
        extra.valorMoraTotal = filteredBoletos.reduce((sum, b) => sum + (b.valorMoraAtual || 0), 0);
        extra.valorAtualizadoTotal = filteredBoletos.reduce((sum, b) => sum + (b.valorTituloAtualizado || 0), 0);
        extra.diasVencidoMedio = total > 0 ? Math.round(filteredBoletos.reduce((sum, b) => sum + (b.qtdDiasVencido || 0), 0) / total) : 0;
        break;
      case 5: // Liquidação
        extra.valorLiquidoTotal = filteredBoletos.reduce((sum, b) => sum + (b.valorLiquido || 0), 0);
        extra.descontoTotal = filteredBoletos.reduce((sum, b) => sum + (b.valorDesconto || 0), 0);
        extra.moraTotal = filteredBoletos.reduce((sum, b) => sum + (b.valorMora || 0), 0);
        break;
      case 6: // Baixa
        // Group by tipoOpFinanceira (motivo)
        break;
    }

    // Count by tipo operação financeira (for types that have it)
    const tiposOp: Record<string, number> = {};
    if ([5, 6].includes(tipoMovimento)) {
      filteredBoletos.forEach(b => {
        const tipo = b.tipoOpFinanceira || 'N/A';
        tiposOp[tipo] = (tiposOp[tipo] || 0) + 1;
      });
    }

    return {
      total,
      valorTituloTotal,
      tarifaTotal,
      tiposOp,
      ...extra,
    };
  }, [filteredBoletos, tipoMovimento]);

  // Unique tipos operação for filter
  const tiposOpUnicos = useMemo(() => {
    const set = new Set(boletos.map(b => b.tipoOpFinanceira).filter(Boolean));
    return Array.from(set).sort();
  }, [boletos]);

  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatCurrency = (value: number | undefined) => {
    if (value === undefined || value === null) return '-';
    return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  const formatDate = (dateStr: string | undefined) => {
    if (!dateStr) return '-';
    try {
      const date = parseISO(dateStr);
      return format(date, 'dd/MM/yyyy');
    } catch {
      return dateStr;
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFilterDataInicio(null);
    setFilterDataFim(null);
    setFilterSituacao('');
    setFilterValorMin('');
    setFilterValorMax('');
  };

  const handleRowClick = (record: BoletoMovimento) => {
    setSelectedRecord(record);
    setDetailOpen(true);
  };

  const handleCloseDetail = () => {
    setDetailOpen(false);
    setSelectedRecord(null);
  };

  const handleCopyJson = () => {
    if (selectedRecord) {
      navigator.clipboard.writeText(JSON.stringify(selectedRecord, null, 2));
      toast.success('JSON copiado para a área de transferência');
    }
  };

  // Render cell value based on column type
  const renderCellValue = (row: BoletoMovimento, columnId: string) => {
    const value = (row as any)[columnId];

    // Handle special columns
    switch (columnId) {
      case 'numeroTitulo':
        return (
          <Typography variant="body2" fontFamily="monospace">
            {value}
          </Typography>
        );
      case 'seuNumero':
        return value || '-';
      case 'dataVencimentoTitulo':
      case 'dataLiquidacao':
      case 'dataPrevisaoCredito':
      case 'dataEmissaoDocumento':
      case 'dataMovimentoEntrada':
      case 'dataLimitePagamento':
        return formatDate(value);
      case 'valorTitulo':
      case 'valorTarifaMovimento':
      case 'valorDesconto':
      case 'valorMora':
      case 'valorMultaAtual':
      case 'valorMoraAtual':
        return formatCurrency(value);
      case 'valorLiquido':
        return (
          <Typography fontWeight="bold" color="success.main">
            {formatCurrency(value)}
          </Typography>
        );
      case 'valorTituloAtualizado':
        return (
          <Typography fontWeight="bold" color="error.main">
            {formatCurrency(value)}
          </Typography>
        );
      case 'qtdDiasVencido':
        return (
          <Chip
            label={`${value} dias`}
            size="small"
            color={value > 30 ? 'error' : value > 15 ? 'warning' : 'default'}
          />
        );
      case 'tipoOpFinanceira':
        return (
          <Tooltip title={value || 'N/A'}>
            <Typography variant="body2" noWrap sx={{ maxWidth: 120 }}>
              {value || 'N/A'}
            </Typography>
          </Tooltip>
        );
      case 'hasQrCode':
        return row.qrCode ? (
          <Chip label="Sim" size="small" color="success" variant="outlined" />
        ) : (
          <Chip label="Não" size="small" color="default" variant="outlined" />
        );
      default:
        return value !== undefined && value !== null ? String(value) : '-';
    }
  };

  const exportToCSV = () => {
    const headers = [
      'Nosso Número',
      'Seu Número',
      'Código Barras',
      'Vencimento',
      'Liquidação',
      'Previsão Crédito',
      'Valor Título',
      'Valor Líquido',
      'Desconto',
      'Mora/Juros',
      'Tarifa',
      'Abatimento',
      'Forma Pagamento',
      'Banco Recebedor',
    ];

    const rows = sortedBoletos.map(b => [
      b.numeroTitulo,
      b.seuNumero || '',
      b.codigoBarras || '',
      b.dataVencimentoTitulo || '',
      b.dataLiquidacao || '',
      b.dataPrevisaoCredito || '',
      b.valorTitulo || 0,
      b.valorLiquido || 0,
      b.valorDesconto || 0,
      b.valorMora || 0,
      b.valorTarifaMovimento || 0,
      b.valorAbatimento || 0,
      b.tipoOpFinanceira || '',
      b.numeroBancoRecebedor || '',
    ]);

    const csvContent = [
      headers.join(';'),
      ...rows.map(row => row.join(';')),
    ].join('\n');

    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `movimentacao_${codigoSolicitacao}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success('CSV exportado com sucesso');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 400 }}>
        <CircularProgress size={48} />
        <Typography sx={{ mt: 2 }}>Carregando dados da movimentação...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/boletos/movimentacoes')} sx={{ mb: 2 }}>
          Voltar
        </Button>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const visibleRows = sortedBoletos.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate('/boletos/movimentacoes')}>
            <ArrowBack />
          </IconButton>
          <Box>
            <Typography variant="h5" fontWeight="bold">
              <Description sx={{ mr: 1, verticalAlign: 'middle' }} />
              {TIPO_MOVIMENTO_LABELS[tipoMovimento] || 'Movimentação'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Solicitação #{codigoSolicitacao} • Arquivo {idArquivo}
              {fileName && ` • ${fileName}`}
            </Typography>
          </Box>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<Refresh />} onClick={loadData}>
            Recarregar
          </Button>
          <Button variant="contained" startIcon={<FileDownload />} onClick={exportToCSV}>
            Exportar CSV
          </Button>
        </Stack>
      </Box>

      {/* Summary Cards - Dynamic based on movement type */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* Common cards */}
        <Grid item xs={6} sm={4} md={2}>
          <Card>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">Total Registros</Typography>
              <Typography variant="h5" fontWeight="bold">{summary.total.toLocaleString()}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">Valor Títulos</Typography>
              <Typography variant="h6" fontWeight="bold" color="text.primary">
                {formatCurrency(summary.valorTituloTotal)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Type 1 (Entrada) specific */}
        {tipoMovimento === 1 && (
          <>
            <Grid item xs={6} sm={4} md={2}>
              <Card sx={{ bgcolor: alpha(theme.palette.success.main, 0.1) }}>
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography variant="caption" color="text.secondary">Com PIX</Typography>
                  <Typography variant="h6" fontWeight="bold" color="success.main">
                    {summary.comPix || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Card>
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography variant="caption" color="text.secondary">Sem PIX</Typography>
                  <Typography variant="h6" fontWeight="bold">
                    {summary.semPix || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </>
        )}

        {/* Type 4 (Vencido) specific */}
        {tipoMovimento === 4 && (
          <>
            <Grid item xs={6} sm={4} md={2}>
              <Card sx={{ bgcolor: alpha(theme.palette.error.main, 0.1) }}>
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography variant="caption" color="text.secondary">Valor Atualizado</Typography>
                  <Typography variant="h6" fontWeight="bold" color="error.main">
                    {formatCurrency(summary.valorAtualizadoTotal)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Card>
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography variant="caption" color="text.secondary">Multas</Typography>
                  <Typography variant="h6" fontWeight="bold" color="warning.main">
                    {formatCurrency(summary.valorMultaTotal)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Card>
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography variant="caption" color="text.secondary">Mora</Typography>
                  <Typography variant="h6" fontWeight="bold" color="info.main">
                    {formatCurrency(summary.valorMoraTotal)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Card>
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography variant="caption" color="text.secondary">Dias Vencido (Média)</Typography>
                  <Typography variant="h6" fontWeight="bold">
                    {summary.diasVencidoMedio} dias
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </>
        )}

        {/* Type 5 (Liquidação) specific */}
        {tipoMovimento === 5 && (
          <>
            <Grid item xs={6} sm={4} md={2}>
              <Card sx={{ bgcolor: alpha(theme.palette.success.main, 0.1) }}>
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography variant="caption" color="text.secondary">Valor Líquido</Typography>
                  <Typography variant="h6" fontWeight="bold" color="success.main">
                    {formatCurrency(summary.valorLiquidoTotal)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Card>
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography variant="caption" color="text.secondary">Descontos</Typography>
                  <Typography variant="h6" fontWeight="bold" color="warning.main">
                    {formatCurrency(summary.descontoTotal)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Card>
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography variant="caption" color="text.secondary">Mora/Juros</Typography>
                  <Typography variant="h6" fontWeight="bold" color="info.main">
                    {formatCurrency(summary.moraTotal)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </>
        )}

        {/* Tarifas - for types that have it */}
        {[1, 5, 6].includes(tipoMovimento) && (
          <Grid item xs={6} sm={4} md={2}>
            <Card>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="caption" color="text.secondary">Tarifas</Typography>
                <Typography variant="h6" fontWeight="bold" color="error.main">
                  {formatCurrency(summary.tarifaTotal)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
          onClick={() => setShowFilters(!showFilters)}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <FilterList />
            <Typography variant="subtitle1" fontWeight="bold">Filtros</Typography>
            {(searchTerm || filterDataInicio || filterDataFim || filterSituacao || filterValorMin || filterValorMax) && (
              <Chip label="Ativos" size="small" color="primary" />
            )}
          </Stack>
          {showFilters ? <ExpandLess /> : <ExpandMore />}
        </Box>

        <Collapse in={showFilters}>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Buscar"
                placeholder="Nosso número, seu número, código de barras..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <DatePicker
                label="Data Inicial"
                value={filterDataInicio}
                onChange={setFilterDataInicio}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <DatePicker
                label="Data Final"
                value={filterDataFim}
                onChange={setFilterDataFim}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Forma Pgto</InputLabel>
                <Select
                  value={filterSituacao}
                  label="Forma Pgto"
                  onChange={(e) => setFilterSituacao(e.target.value)}
                >
                  <MenuItem value="">Todas</MenuItem>
                  {tiposOpUnicos.map(tipo => (
                    <MenuItem key={tipo} value={tipo}>{tipo}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={2}>
              <Stack direction="row" spacing={1}>
                <TextField
                  size="small"
                  label="Valor Min"
                  type="number"
                  value={filterValorMin}
                  onChange={(e) => setFilterValorMin(e.target.value)}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                  }}
                  sx={{ flex: 1 }}
                />
                <TextField
                  size="small"
                  label="Valor Max"
                  type="number"
                  value={filterValorMax}
                  onChange={(e) => setFilterValorMax(e.target.value)}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                  }}
                  sx={{ flex: 1 }}
                />
              </Stack>
            </Grid>
          </Grid>

          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Exibindo {filteredBoletos.length} de {boletos.length} registros
            </Typography>
            <Button size="small" onClick={clearFilters}>
              Limpar Filtros
            </Button>
          </Box>
        </Collapse>
      </Paper>

      {/* Tipos Operação Summary */}
      {Object.keys(summary.tiposOp).length > 0 && (
        <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
          {Object.entries(summary.tiposOp).map(([tipo, count]) => (
            <Chip
              key={tipo}
              label={`${tipo}: ${count}`}
              size="small"
              variant={filterSituacao === tipo ? 'filled' : 'outlined'}
              color={filterSituacao === tipo ? 'primary' : 'default'}
              onClick={() => setFilterSituacao(filterSituacao === tipo ? '' : tipo)}
            />
          ))}
        </Stack>
      )}

      {/* Data Table */}
      <Paper>
        <TableContainer sx={{ maxHeight: 600 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {headCells.map((headCell) => (
                  <TableCell
                    key={headCell.id}
                    align={headCell.numeric ? 'right' : 'left'}
                    sortDirection={orderBy === headCell.id ? order : false}
                    sx={{ fontWeight: 600, bgcolor: 'background.paper' }}
                  >
                    <TableSortLabel
                      active={orderBy === headCell.id}
                      direction={orderBy === headCell.id ? order : 'asc'}
                      onClick={() => handleRequestSort(headCell.id)}
                    >
                      {headCell.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={headCells.length} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">Nenhum registro encontrado</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                visibleRows.map((row, index) => (
                  <TableRow
                    hover
                    key={row.numeroTitulo || index}
                    onClick={() => handleRowClick(row)}
                    sx={{ '&:last-child td': { border: 0 }, cursor: 'pointer' }}
                  >
                    {headCells.map((cell) => (
                      <TableCell
                        key={cell.id}
                        align={cell.numeric ? 'right' : 'left'}
                      >
                        {renderCellValue(row, cell.id)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={filteredBoletos.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Linhas por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
        />
      </Paper>

      {/* Detail Modal */}
      <Dialog
        open={detailOpen}
        onClose={handleCloseDetail}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            Registro #{selectedRecord?.numeroTitulo}
          </Typography>
          <IconButton onClick={handleCloseDetail} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box
            component="pre"
            sx={{
              bgcolor: 'grey.900',
              color: 'grey.100',
              p: 2,
              borderRadius: 1,
              overflow: 'auto',
              maxHeight: 500,
              fontSize: '0.85rem',
              fontFamily: 'monospace',
              m: 0,
            }}
          >
            {selectedRecord ? JSON.stringify(selectedRecord, null, 2) : ''}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button startIcon={<ContentCopy />} onClick={handleCopyJson}>
            Copiar JSON
          </Button>
          <Button variant="contained" onClick={handleCloseDetail}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BoletoMovimentacaoViewerPage;
